import { useEffect, useMemo, useState } from 'react'
import { get } from 'aws-amplify/api'
import { AlertCircle, RefreshCw, Server } from 'lucide-react'
import { useAuth } from '@/lib/auth/AuthContext'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import LoadingSpinner from '@/components/LoadingSpinner'
import { useToast } from '@/hooks/use-toast'

interface EcsTaskContainer {
  name?: string
  lastStatus?: string
  healthStatus?: string
  reason?: string
  exitCode?: number
  image?: string
}

interface EcsTaskSummary {
  taskArn?: string
  taskDefinitionArn?: string
  lastStatus?: string
  desiredStatus?: string
  healthStatus?: string
  launchType?: string
  group?: string
  connectivity?: string
  startedAt?: string
  stoppedAt?: string
  cpu?: string
  memory?: string
  serviceName?: string
  containers: EcsTaskContainer[]
}

interface EcsFailure {
  arn?: string
  reason?: string
  detail?: string
}

interface EcsClusterStatus {
  cluster: string
  clusterName: string
  environment: 'prod' | 'uat' | 'dev' | 'unknown'
  clusterStatus: string
  activeServicesCount: number
  runningTasksCount: number
  pendingTasksCount: number
  registeredContainerInstancesCount: number
  capacityProviders: string[]
  taskArns: string[]
  pendingTaskArns: string[]
  tasks: EcsTaskSummary[]
  failures: EcsFailure[]
  error?: string
}

interface EventBridgeRule {
  Name?: string
  State?: string
  ScheduleExpression?: string
}

const environmentBadgeClass: Record<EcsClusterStatus['environment'], string> = {
  prod: 'bg-rose-500/15 text-rose-100 border-rose-500/20',
  uat: 'bg-amber-500/15 text-amber-100 border-amber-500/20',
  dev: 'bg-sky-500/15 text-sky-100 border-sky-500/20',
  unknown: 'bg-slate-600/40 text-slate-100 border-slate-500/20'
}

const clusterStatusClass = (status: string) => {
  const normalized = status.toUpperCase()

  if (normalized === 'ACTIVE') {
    return 'bg-emerald-500/15 text-emerald-100 border-emerald-500/20'
  }

  if (normalized === 'PROVISIONING') {
    return 'bg-amber-500/15 text-amber-100 border-amber-500/20'
  }

  if (normalized === 'ERROR') {
    return 'bg-rose-500/15 text-rose-100 border-rose-500/20'
  }

  return 'bg-slate-600/40 text-slate-100 border-slate-500/20'
}

export default function InfrastructurePage() {
  const { isAuthenticated, session } = useAuth()
  const { toast } = useToast()
  const [ecsData, setEcsData] = useState<EcsClusterStatus[]>([])
  const [eventBridgeData, setEventBridgeData] = useState<EventBridgeRule[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null)

  const summary = useMemo(() => {
    const totalRunningTasks = ecsData.reduce((acc, cluster) => acc + cluster.runningTasksCount, 0)
    const totalPendingTasks = ecsData.reduce((acc, cluster) => acc + cluster.pendingTasksCount, 0)
    const activeServices = ecsData.reduce((acc, cluster) => acc + cluster.activeServicesCount, 0)
    const unhealthyClusters = ecsData.filter(
      (cluster) => cluster.clusterStatus.toUpperCase() === 'ERROR' || !!cluster.error
    ).length

    return {
      clusterCount: ecsData.length,
      totalRunningTasks,
      totalPendingTasks,
      activeServices,
      unhealthyClusters
    }
  }, [ecsData])

  const fetchInfrastructureData = async () => {
    try {
      setIsLoading(true)
      setError(null)

      const [ecsResponse, eventBridgeResponse] = await Promise.all([
        get({
          apiName: 'dashboard',
          path: '/data/ecs',
          options: {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${session?.tokens?.idToken}`
            },
            withCredentials: true
          }
        }).response,
        get({
          apiName: 'dashboard',
          path: '/data/eventBridge',
          options: {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${session?.tokens?.idToken}`
            },
            withCredentials: true
          }
        }).response
      ])

      const ecsPayload = (await ecsResponse.body.json()) as unknown
      const eventBridgePayload = (await eventBridgeResponse.body.json()) as unknown

      setEcsData(Array.isArray(ecsPayload) ? (ecsPayload as EcsClusterStatus[]) : [])
      setEventBridgeData(Array.isArray(eventBridgePayload) ? (eventBridgePayload as EventBridgeRule[]) : [])
      setLastRefresh(new Date())
    } catch (fetchError) {
      console.error('Error fetching infrastructure data:', fetchError)
      const message = fetchError instanceof Error ? fetchError.message : 'Failed to fetch infrastructure data'
      setError(message)

      toast({
        title: 'Error',
        description: 'Failed to fetch infrastructure data',
        variant: 'destructive'
      })
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (isAuthenticated) {
      fetchInfrastructureData()
    }
  }, [isAuthenticated])

  if (isLoading && ecsData.length === 0) {
    return (
      <div className="flex h-[50vh] items-center justify-center">
        <LoadingSpinner size="lg" message="Loading infrastructure data..." />
      </div>
    )
  }

  const scheduledRules = eventBridgeData.filter((rule) => !!rule.ScheduleExpression).length

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Infrastructure</h1>
          <p className="text-sm text-slate-400">
            ECS cluster health, running tasks, and schedule coverage.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="text-white"
          onClick={fetchInfrastructureData}
          disabled={isLoading}
        >
          <RefreshCw className={`mr-2 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          {isLoading ? 'Refreshing...' : 'Refresh now'}
        </Button>
      </div>

      {lastRefresh && (
        <p className="text-xs text-slate-400">
          Last refreshed: {lastRefresh.toLocaleString()}
        </p>
      )}

      {error ? (
        <Card className="border-rose-500/40 bg-rose-500/10">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-rose-100">
              <AlertCircle className="h-5 w-5" />
              <p className="font-medium">Unable to load infrastructure details</p>
            </div>
            <p className="mt-1 text-sm text-rose-100/90">{error}</p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
            <Card className="border-slate-800 bg-slate-900/70">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-slate-300">Clusters</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold text-slate-100">{summary.clusterCount}</p>
              </CardContent>
            </Card>

            <Card className="border-slate-800 bg-slate-900/70">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-slate-300">Running tasks</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold text-emerald-200">{summary.totalRunningTasks}</p>
              </CardContent>
            </Card>

            <Card className="border-slate-800 bg-slate-900/70">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-slate-300">Pending tasks</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold text-amber-200">{summary.totalPendingTasks}</p>
              </CardContent>
            </Card>

            <Card className="border-slate-800 bg-slate-900/70">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-slate-300">Active services</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold text-slate-100">{summary.activeServices}</p>
              </CardContent>
            </Card>

            <Card className="border-slate-800 bg-slate-900/70">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-slate-300">Cluster issues</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold text-rose-200">{summary.unhealthyClusters}</p>
              </CardContent>
            </Card>
          </div>

          <Card className="border-slate-800 bg-slate-900/70">
            <CardHeader>
              <CardTitle className="text-slate-100">EventBridge overview</CardTitle>
              <CardDescription className="text-slate-400">
                {eventBridgeData.length} total rules, {scheduledRules} scheduled rules.
              </CardDescription>
            </CardHeader>
          </Card>

          <div className="space-y-4">
            {ecsData.map((cluster) => (
              <Card key={cluster.cluster} className="border-slate-800 bg-slate-900/70">
                <CardHeader className="space-y-3">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <CardTitle className="flex items-center gap-2 text-slate-100">
                        <Server className="h-4 w-4" />
                        {cluster.clusterName}
                      </CardTitle>
                      <CardDescription className="mt-1 font-mono text-xs text-slate-400">
                        {cluster.cluster}
                      </CardDescription>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge className={clusterStatusClass(cluster.clusterStatus)}>{cluster.clusterStatus}</Badge>
                      <Badge className={environmentBadgeClass[cluster.environment]}>
                        {cluster.environment.toUpperCase()}
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {cluster.error && (
                    <div className="rounded-md border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-xs text-rose-100">
                      {cluster.error}
                    </div>
                  )}

                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    <div className="rounded-md border border-slate-800 bg-slate-950/40 p-3">
                      <p className="text-xs text-slate-400">Running tasks</p>
                      <p className="text-xl font-semibold text-emerald-200">{cluster.runningTasksCount}</p>
                    </div>
                    <div className="rounded-md border border-slate-800 bg-slate-950/40 p-3">
                      <p className="text-xs text-slate-400">Pending tasks</p>
                      <p className="text-xl font-semibold text-amber-200">{cluster.pendingTasksCount}</p>
                    </div>
                    <div className="rounded-md border border-slate-800 bg-slate-950/40 p-3">
                      <p className="text-xs text-slate-400">Active services</p>
                      <p className="text-xl font-semibold text-slate-100">{cluster.activeServicesCount}</p>
                    </div>
                    <div className="rounded-md border border-slate-800 bg-slate-950/40 p-3">
                      <p className="text-xs text-slate-400">Container instances</p>
                      <p className="text-xl font-semibold text-slate-100">{cluster.registeredContainerInstancesCount}</p>
                    </div>
                  </div>

                  <div className="grid gap-3 lg:grid-cols-2">
                    <div className="rounded-md border border-slate-800 bg-slate-950/40 p-3">
                      <p className="text-xs uppercase tracking-wide text-slate-400">Capacity providers</p>
                      {cluster.capacityProviders.length > 0 ? (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {cluster.capacityProviders.map((provider) => (
                            <Badge key={provider} variant="outline" className="text-slate-200">
                              {provider}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <p className="mt-2 text-xs text-slate-500">No capacity providers reported</p>
                      )}
                    </div>

                    <div className="rounded-md border border-slate-800 bg-slate-950/40 p-3">
                      <p className="text-xs uppercase tracking-wide text-slate-400">Task ARN inventory</p>
                      {cluster.taskArns.length > 0 ? (
                        <ul className="mt-2 space-y-1 text-xs text-slate-200">
                          {cluster.taskArns.slice(0, 4).map((taskArn) => (
                            <li key={taskArn} className="truncate font-mono">
                              {taskArn}
                            </li>
                          ))}
                          {cluster.taskArns.length > 4 && (
                            <li className="text-slate-500">+{cluster.taskArns.length - 4} more running task ARNs</li>
                          )}
                        </ul>
                      ) : (
                        <p className="mt-2 text-xs text-slate-500">No running task ARNs in this cluster</p>
                      )}
                    </div>
                  </div>

                  {cluster.failures.length > 0 && (
                    <div className="rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-100">
                      <p className="font-semibold">Task lookup failures</p>
                      <ul className="mt-2 space-y-1">
                        {cluster.failures.map((failure, index) => (
                          <li key={`${failure.arn ?? 'unknown'}-${index}`} className="font-mono">
                            {(failure.arn ?? 'unknown-arn')} • {failure.reason ?? 'Unknown reason'}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="rounded-md border border-slate-800 bg-slate-950/40 p-3">
                    <p className="text-xs uppercase tracking-wide text-slate-400">Task detail</p>
                    {cluster.tasks.length > 0 ? (
                      <div className="mt-2 space-y-2">
                        {cluster.tasks.slice(0, 8).map((task) => (
                          <div
                            key={task.taskArn}
                            className="rounded-md border border-slate-800 bg-slate-900/60 p-3 text-xs text-slate-200"
                          >
                            <div className="flex flex-wrap items-center gap-2">
                              <Badge variant="outline" className="text-slate-100">{task.lastStatus ?? 'Unknown'}</Badge>
                              <span className="text-slate-400">{task.serviceName ?? task.group ?? 'service:unknown'}</span>
                            </div>
                            <p className="mt-2 break-all font-mono text-[11px] text-slate-400">{task.taskArn}</p>
                            <div className="mt-2 grid gap-1 sm:grid-cols-2 lg:grid-cols-4">
                              <p>Desired: <span className="text-slate-100">{task.desiredStatus ?? '—'}</span></p>
                              <p>Health: <span className="text-slate-100">{task.healthStatus ?? '—'}</span></p>
                              <p>Launch: <span className="text-slate-100">{task.launchType ?? '—'}</span></p>
                              <p>Started: <span className="text-slate-100">{task.startedAt ? new Date(task.startedAt).toLocaleString() : '—'}</span></p>
                            </div>
                          </div>
                        ))}
                        {cluster.tasks.length > 8 && (
                          <p className="text-xs text-slate-500">+{cluster.tasks.length - 8} additional tasks not shown</p>
                        )}
                      </div>
                    ) : (
                      <p className="mt-2 text-xs text-slate-500">No task details available for this cluster</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}

            {ecsData.length === 0 && !isLoading && (
              <Card className="border-slate-800 bg-slate-900/70">
                <CardContent className="pt-6">
                  <p className="text-center text-slate-400">No ECS clusters returned from the API.</p>
                </CardContent>
              </Card>
            )}
          </div>
        </>
      )}
    </div>
  )
}
